import React, {useState, useEffect, MutableRefObject} from 'react';
import {nanoid} from 'nanoid';
import CircularProgress from '@material-ui/core/CircularProgress';
import {Prompt} from 'react-router';
import DeleteDialog from '@pluto/delete-dialog/DeleteDialog';
import wrap from 'wraptile';
import {curry} from 'ramda';
import {
    MetadataGeneral,
    MetadataLocalized,
    MetadataTerritorial,
    Role,
    SetQcMetadataType,
    MetadataContentType,
    CategoryItem,
    Descriptor,
    VideoTypeData,
} from './ContentItemViewMetadataTypes';
import QCMetadataGeneral from './ContentItemViewMetadataGeneral';
import ContentItemViewMetadataLocalized, {MetadataUpdateHandlers,} from './ContentItemViewMetadataLocalized';
import ContentItemViewMetadataTerritorial from './ContentItemViewMetadataTerritorial';
import {Genre} from '../../types';
import './ContentItemViewMetadata.scss';

export interface TerritorialMetadataUpdateHandlers {
    id: number;
    body: MetadataTerritorial[];
}

export interface ContentItemViewMetadataContainerProps {
    categories: CategoryItem[] | null;
    isLoadingCategories: boolean;
    setMetadataCategories: any;
    isLoadingGenres: boolean;
    genres: Genre[];
    setQcGenres: any;
    isGeneralMetadataEditMode: boolean;
    setIsGeneralMetadataEditMode: (value: boolean) => void;
    isLocalizedMetadataEditMode: boolean;
    setIsLocalizedMetadataEditMode: (value: boolean) => void;
    isTerritorialMetadataEditMode: boolean;
    setIsTerritorialMetadataEditMode: (value: boolean) => void;
    setVideoData: (id: number) => void;
    general: MetadataGeneral;
    localized: MetadataLocalized;
    territorial: MetadataTerritorial;
    isLoading: boolean;
    id: number;
    allotment: number | null;
    setContentItemViewMetadataGeneral: (id: number, param: any) => void;
    updateLocalizedMetadata: (params: MetadataUpdateHandlers) => void;
    updateTerritorialMetadata: (params: TerritorialMetadataUpdateHandlers) => void;
    reloadRef: MutableRefObject<boolean>;
    ratingDescriptors: Descriptor[];
    setQcDescriptors: any;
    isLoadingDescriptors: boolean;
    videoData: {
        video: VideoTypeData;
    };
    jobDeliveryDate: string;
    contentItemViewMetadataDraft: boolean;
}

export const handleFetchQcMetadata = wrap(
    (
        setQcMetadata: SetQcMetadataType,
        resetQcMetadata: () => void,
        id: number,
        isLoading: boolean
    ) => {
        if (isLoading) setQcMetadata(id);

        return resetQcMetadata;
    }
);

export const mapPartItem = curry(
    (label: string, value: string): JSX.Element => {
        return (
            <div className="part-wrapper" key={nanoid()}>
                <div className="part-label">{label}</div>
                <div className="part-value">{value}</div>
            </div>
        );
    }
);

export const getPartItems = (label: string, values: string[]): JSX.Element[] => {
    return values.map(mapPartItem(label));
};

export const mapToRolesObject = curry((role: string, {id, label}: Role) => {
    return {
        id,
        role,
        name: label,
    };
});

export const handleBlockedNavigation = curry(
    (
        confirmedNavigation: boolean,
        isGeneralEditMode: boolean,
        isLocalizedEditMode: boolean,
        isTerritorialEditMode: boolean,
        setModalVisible: (value: boolean) => void,
        setLastLocation: (newLocation: Location | null) => void,
        nextLocation: Location | null
    ): boolean => {
        if (
            !confirmedNavigation &&
            (isGeneralEditMode || isLocalizedEditMode || isTerritorialEditMode)
        ) {
            setModalVisible(true);
            setLastLocation(nextLocation);
            return false;
        }

        return true;
    }
);

export const handleConfirmNavigationClick = wrap(
    (
        setModalVisible: (value: boolean) => void,
        setConfirmedNavigation: (value: boolean) => void
    ) => {
        setModalVisible(false);
        setConfirmedNavigation(true);
    }
);

export const handleCloseModal = wrap((setModalVisible: (value: boolean) => void) => {
    setModalVisible(false);
});

export const removeEventListener = () => {
    window.removeEventListener('beforeunload', () => {});
};

export const handleUseEffect = wrap(
    (beforeUnloadListener: any, reloadRef: MutableRefObject<boolean>) => {
        window.addEventListener('beforeunload', beforeUnloadListener(reloadRef));

        return removeEventListener;
    }
);

export const beforeUnloadListener = curry(
    (reloadRef: MutableRefObject<boolean>, event: any) => {
        if (reloadRef.current === true) {
            return null;
        }

        event.preventDefault();
        event.returnValue = '';
    }
);

export const reloadEffect = wrap(
    (
        confirmedNavigation: boolean,
        lastLocation: Location,
        reloadRef: MutableRefObject<boolean>
    ) => {
        if (confirmedNavigation && lastLocation) {
            reloadRef.current = true;
            window.location.replace(lastLocation.pathname);
        }
    }
);

export const reloadRefEffect = wrap(
    (
        reloadRef: MutableRefObject<boolean>,
        isGeneralEditMode: boolean,
        isLocalizedEditMode: boolean,
        isTerritorialMetadataEditMode: boolean
    ) => {
        reloadRef.current =
            !isGeneralEditMode && !isLocalizedEditMode && !isTerritorialMetadataEditMode;
    }
);

export const handleFetchGenres = wrap((isLoadingGenres: boolean, setQcGenres: any) => {
    if (isLoadingGenres) {
        setQcGenres();
    }
});

export const handleFetchVideoData = wrap(
    (setVideoData: (id: number) => void, id: number) => {
        setVideoData(id);
    }
);

export const handleFetchDescriptors = wrap(
    (isLoadingDescriptors: boolean, setQcDescriptors: any) => {
        if (isLoadingDescriptors) {
            setQcDescriptors();
        }
    }
);

export const handleFetchCategories = wrap(
    (isLoadingCategories: boolean, setMetadataCategories: any) => {
        if (isLoadingCategories) {
            setMetadataCategories();
        }
    }
);

export const getConfirmationDialog = (
    modalVisible: boolean,
    setModalVisible: (value: boolean) => void,
    setConfirmedNavigation: (value: boolean) => void,
    handleCloseModal: any,
    handleConfirmNavigationClick: any
) => {
    return modalVisible ? (
        <DeleteDialog
            onClose={handleCloseModal(setModalVisible)}
            onConfirm={handleConfirmNavigationClick(
                setModalVisible,
                setConfirmedNavigation
            )}
            text="Are you sure you want to leave this page?"
            secondaryText="Changes you made will not be saved"
            cancelText="STAY"
            confirmText="LEAVE"
        />
    ) : null;
};

export const getMetadataContentType = (
    contentType: string | undefined
): MetadataContentType => {
    switch (contentType) {
        case 'web_originals':
            return MetadataContentType.WebOriginals;
        case 'episode':
            return MetadataContentType.Episode;
        case 'film':
            return MetadataContentType.Film;
        case 'series':
            return MetadataContentType.Series;
        default:
            return MetadataContentType.WebOriginals;
    }
};

export const mapActors = mapToRolesObject('actor');
export const mapDirectors = mapToRolesObject('director');
export const mapProducers = mapToRolesObject('producer');
export const mapComposers = mapToRolesObject('composer');
export const mapWriters = mapToRolesObject('writer');

function ContentItemViewMetadataContainer(props: ContentItemViewMetadataContainerProps) {
    const {
        isLoadingGenres,
        genres,
        setQcGenres,
        isGeneralMetadataEditMode,
        isLocalizedMetadataEditMode,
        isTerritorialMetadataEditMode,
        reloadRef,
        isLoading,
        setIsGeneralMetadataEditMode,
        setIsLocalizedMetadataEditMode,
        setIsTerritorialMetadataEditMode,
        general,
        localized,
        territorial,
        id,
        allotment,
        setContentItemViewMetadataGeneral,
        updateLocalizedMetadata,
        categories,
        isLoadingCategories,
        setMetadataCategories,
        ratingDescriptors,
        setQcDescriptors,
        isLoadingDescriptors,
        updateTerritorialMetadata,
        videoData,
        jobDeliveryDate,
    } = props;
    const {roles, contentType} = general;
    const [modalVisible, setModalVisible] = useState(false);
    const [lastLocation, setLastLocation] = useState<Location | null>(null);
    const [confirmedNavigation, setConfirmedNavigation] = useState(false);

    useEffect(reloadEffect(confirmedNavigation, lastLocation, reloadRef), [
        confirmedNavigation,
    ]);

    useEffect(
        reloadRefEffect(
            reloadRef,
            isGeneralMetadataEditMode,
            isLocalizedMetadataEditMode,
            isTerritorialMetadataEditMode
        ),
        [
            isGeneralMetadataEditMode,
            isLocalizedMetadataEditMode,
            isTerritorialMetadataEditMode,
        ]
    );

    useEffect(handleFetchGenres(isLoadingGenres, setQcGenres), []);

    useEffect(handleFetchDescriptors(isLoadingDescriptors, setQcDescriptors), []);

    useEffect(handleFetchCategories(isLoadingCategories, setMetadataCategories), []);

    useEffect(handleUseEffect(beforeUnloadListener, reloadRef), []);

    const metadataContentType = getMetadataContentType(contentType);

    const {actors, directors, producers, composers, writers} = roles;

    const actorsInitialValues = actors.map(mapActors);
    const directorsInitialValues = directors.map(mapDirectors);
    const producersInitialValues = producers.map(mapProducers);
    const composersInitialValues = composers.map(mapComposers);
    const writersInitialValues = writers.map(mapWriters);

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <div className="content-item-view-metadata">
            <div className="qc-metadata">
                <Prompt
                    when={
                        isGeneralMetadataEditMode ||
                        isLocalizedMetadataEditMode ||
                        isTerritorialMetadataEditMode
                    }
                    message={handleBlockedNavigation(
                        confirmedNavigation,
                        isGeneralMetadataEditMode,
                        isLocalizedMetadataEditMode,
                        isTerritorialMetadataEditMode,
                        setModalVisible,
                        setLastLocation
                    )}
                />
                {getConfirmationDialog(
                    modalVisible,
                    setModalVisible,
                    setConfirmedNavigation,
                    handleCloseModal,
                    handleConfirmNavigationClick
                )}
                <div className="qc-metadata__general">
                    <QCMetadataGeneral
                        jobDeliveryDate={jobDeliveryDate}
                        allotment={allotment}
                        genres={genres}
                        videoData={videoData}
                        metadataContentType={metadataContentType}
                        isEditMode={isGeneralMetadataEditMode}
                        setIsEditMode={setIsGeneralMetadataEditMode}
                        general={general}
                        taskId={id}
                        actorsInitialValues={actorsInitialValues}
                        directorsInitialValues={directorsInitialValues}
                        producersInitialValues={producersInitialValues}
                        composersInitialValues={composersInitialValues}
                        writersInitialValues={writersInitialValues}
                        setContentItemViewMetadataGeneral={
                            setContentItemViewMetadataGeneral
                        }
                    />
                </div>
                <div className="qc-metadata__localized">
                    <ContentItemViewMetadataLocalized
                        jobDeliveryDate={jobDeliveryDate}
                        categories={categories}
                        metadataContentType={metadataContentType}
                        localized={localized}
                        updateLocalizedMetadata={updateLocalizedMetadata}
                        editable={isLocalizedMetadataEditMode}
                        setEditable={setIsLocalizedMetadataEditMode}
                    />
                </div>
                <div className="qc-metadata__territorial">
                    <ContentItemViewMetadataTerritorial
                        jobDeliveryDate={jobDeliveryDate}
                        territorial={territorial}
                        taskId={id}
                        isEditMode={isTerritorialMetadataEditMode}
                        setIsEditMode={setIsTerritorialMetadataEditMode}
                        ratingDescriptors={ratingDescriptors}
                        updateTerritorialMetadata={updateTerritorialMetadata}
                    />
                </div>
            </div>
        </div>
    );
}

export default React.memo(ContentItemViewMetadataContainer);
